/**
 * Parses an Anthropic text/event-stream response body and yields
 * each text delta as it arrives.
 *
 * Anthropic SSE format — relevant event types:
 *   event: content_block_delta
 *   data: {"type":"content_block_delta","index":0,"delta":{"type":"text_delta","text":"..."}}
 *
 *   event: message_stop
 *   data: {"type":"message_stop"}
 *
 * All other event types (message_start, content_block_start/stop, ping,
 * error) are passed through as-is via the `event` field so callers can
 * react to errors or completion.
 */

export interface SseTextDelta {
  type: 'text_delta';
  text: string;
}

export interface SseEvent {
  event: string;
  data: unknown;
}

/**
 * Yields parsed SSE events from an Anthropic streaming response body.
 * Throws if the stream contains an `error` event.
 */
export async function* parseAnthropicSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by blank lines (\n\n).
      const events = buffer.split('\n\n');
      // Keep the last (potentially incomplete) chunk in the buffer.
      buffer = events.pop() ?? '';

      for (const raw of events) {
        const parsed = parseSseBlock(raw);
        if (!parsed) continue;
        if (parsed.event === 'error') {
          const errData = parsed.data as { error?: { message?: string } };
          throw new Error(errData?.error?.message ?? 'Upstream SSE error');
        }
        yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Extracts text content from a stream of Anthropic SSE events.
 * Calls `onDelta` for each text fragment; resolves with the full text.
 */
export async function consumeAnthropicStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (text: string) => void,
): Promise<string> {
  let full = '';
  for await (const evt of parseAnthropicSseStream(body)) {
    if (evt.event !== 'content_block_delta') continue;
    const d = evt.data as { delta?: { type?: string; text?: string } };
    if (d?.delta?.type === 'text_delta' && typeof d.delta.text === 'string') {
      onDelta(d.delta.text);
      full += d.delta.text;
    }
  }
  return full;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function parseSseBlock(block: string): SseEvent | null {
  let event = 'message';
  let dataLine = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLine = line.slice('data:'.length).trim();
    }
  }

  if (!dataLine || dataLine === '[DONE]') return null;

  let data: unknown;
  try {
    data = JSON.parse(dataLine);
  } catch {
    return null;
  }

  return { event, data };
}
